class ESASubscriptionWidget extends HTMLElement {
  connectedCallback() {
    this.productUrl = window.location.pathname;

    this.initEvents();
    this.watchUrlChanges();
    this.watchVariantInput();

    setTimeout(() => {
      this.updatePrices();
    }, 2000);
  }

  disconnectedCallback() {
    this.variantInputObserver?.disconnect();
  }

  initEvents() {
    this.addEventListener('change', event => {
      if (event.target.name === 'selling_plan' || event.target.name === 'purchase_type') {
        this.updatePrices();
      }
    });
  }

  watchUrlChanges() {
    if (window.__esaUrlWatcherInitialized) return;

    window.__esaUrlWatcherInitialized = true;

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      window.dispatchEvent(new CustomEvent('esa:url-change'));
    };

    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      window.dispatchEvent(new CustomEvent('esa:url-change'));
    };

    window.addEventListener('popstate', () => {
      window.dispatchEvent(new CustomEvent('esa:url-change'));
    });

    window.addEventListener('esa:url-change', () => {
      this.handleVariantChange();
    });
  }

  watchVariantInput() {
    const variantInput = document.querySelector(
      'form[action*="/cart/add"] [name="id"]'
    );

    if (!variantInput) return;

    let previousValue = variantInput.value;

    variantInput.addEventListener('change', () => {
      if (!this.getVariantIdFromUrl()) {
        this.handleVariantChange();
      }
    });

    this.variantInputObserver = new MutationObserver(() => {
      if (variantInput.value !== previousValue) {
        previousValue = variantInput.value;

        if (!this.getVariantIdFromUrl()) {
          this.handleVariantChange();
        }
      }
    });

    this.variantInputObserver.observe(variantInput, {
      attributes: true,
      attributeFilter: ['value']
    });
  }

  async handleVariantChange() {
    const selections = this.saveSelections();

    let variantId = this.getVariantIdFromUrl();

    if (!variantId) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      variantId = this.getVariantIdFromForm();
    }

    if (!variantId) return;

    await this.refreshWidget(variantId, selections);
  }

  getVariantIdFromUrl() {
    return new URLSearchParams(window.location.search).get('variant');
  }

  getVariantIdFromForm() {
    return document.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
  }

  saveSelections() {
    return {
      purchaseType: this.querySelector('[name="purchase_type"]:checked')?.value,
      sellingPlan: this.querySelector('select[name="selling_plan"]')?.value
    };
  }

  async refreshWidget(variantId, selections) {
    try {
      const response = await fetch(`${this.productUrl}?section_id=${this.dataset.sectionId}&variant=${variantId}`);

      if (!response.ok) return;

      const html = await response.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newWidget = doc.querySelector('esa-subscription-widget');

      if (!newWidget) return;

      this.innerHTML = newWidget.innerHTML;

      this.restoreSelections(selections);

      this.updatePrices();
    } catch (error) {
      console.error('ESA Widget Refresh Error:', error);
    }
  }

  restoreSelections(selections) {
    if (!selections) return;

    const purchaseInput = this.querySelector(`[name="purchase_type"][value="${selections.purchaseType}"]`);

    if (purchaseInput) {
      purchaseInput.checked = true;
      purchaseInput.closest('.esa__option')?.classList.add('is-selected');
    }

    const selectPlan = this.querySelector('select[name="selling_plan"]');

    if (selectPlan && [...selectPlan.options].some(option => option.value === selections.sellingPlan)) {
      selectPlan.value = selections.sellingPlan;
    }
  }

  toNumber(value) {
    if (!value) return 0;

    return Number(
      String(value).replace(/[^0-9.,]/g, '').replace(/,/g, '')
    );
  }

  updatePrices() {
    const purchaseType = this.querySelector('[name="purchase_type"]:checked')?.value;

    this.toggleSellingPlans(purchaseType);

    const selectedPlan = this.querySelector('select[name="selling_plan"] option:checked');
    if (!selectedPlan) return;

    const subscriptionPrice = selectedPlan.dataset.subscriptionPrice;

    const regularPrice = selectedPlan.dataset.price;

    const comparePrice = selectedPlan.dataset.comparePrice;

    this.querySelectorAll('[data-esa-subscription-price]').forEach(el => {
      el.textContent = subscriptionPrice;
    });

    this.querySelectorAll('[data-esa-onetime-price]').forEach(el => {
      el.textContent = regularPrice;
    });

    this.querySelectorAll('[data-esa-compare-price]').forEach(el => {
      el.innerHTML = comparePrice ? `<s>${comparePrice}</s>` : '';
    });

    const saveText = selectedPlan.dataset.save || '';
    const saveBadge = this.querySelector('.esa__save-badge');

    if (saveBadge) {
      saveBadge.textContent = saveText;
      saveBadge.style.display = saveText.trim() ? 'flex' : 'none';
    }
    
    this.querySelectorAll('.esa__price-benefit span').forEach(el => {
      el.textContent = subscriptionPrice;
    });

    const interval = selectedPlan.dataset.interval || '';

    this.querySelectorAll('.esa__interval-benefit span').forEach(el => {
      el.textContent = interval;
    });

    this.updateThemePriceDisplay(purchaseType,regularPrice,subscriptionPrice,comparePrice);

    clearTimeout(this.loyaltyTimeout); 
    this.loyaltyTimeout = setTimeout(() => {
      this.updateLoyaltyPoints(purchaseType === 'subscription' ? subscriptionPrice : regularPrice);
    }, 2000);
  }

  toggleSellingPlans(purchaseType) {
    const sellingPlanSelect = this.querySelector('select[name="selling_plan"]');

    const isSubscription = purchaseType === 'subscription';

    if (!isSubscription) {
      if (sellingPlanSelect) {
        sellingPlanSelect.disabled = true;
      }
    } else {
      if (sellingPlanSelect) {
        sellingPlanSelect.disabled = false;
      }
    }
  }

  updateThemePriceDisplay(purchaseType, regularPrice, subscriptionPrice, comparePrice) {
    document.querySelectorAll('.main-product-price').forEach(priceBlock => {
      const mainPrice = priceBlock.querySelector('.main-price');

      const subPrice = priceBlock.querySelector('.price-subscription');

      const targetPrice = priceBlock.querySelector('.target-price');

      const targetComparePrice = priceBlock.querySelector('.target-compare-at-price');

      if (mainPrice) {
        mainPrice.textContent = regularPrice;
        mainPrice.classList.toggle('active', purchaseType === 'onetime');
      }

      if (subPrice) {
        subPrice.textContent = subscriptionPrice;
        subPrice.classList.toggle('active', purchaseType === 'subscription');
      }

      if (targetPrice) {
        targetPrice.textContent = this.toNumber(purchaseType === 'subscription' ? subscriptionPrice : regularPrice) * 100;
      }

      if (targetComparePrice) {
        targetComparePrice.textContent = comparePrice ? this.toNumber(comparePrice) * 100 : '';
      }
    });
  }

  updateLoyaltyPoints(priceText) {
    const rewardsWidget = document.querySelector('.aaa-lyt-make-a-purchase-point-widget');
    if (!rewardsWidget) return;

    const pointsEl = rewardsWidget.querySelector('b');
    if (!pointsEl) return;

    const POINTS_PER_DOLLAR = 5;

    const price = this.toNumber(priceText);
    if (!price) return;

    const points = Math.ceil(price * POINTS_PER_DOLLAR);

    pointsEl.textContent = points;
  }
}

if (!customElements.get('esa-subscription-widget')) {
  customElements.define('esa-subscription-widget', ESASubscriptionWidget);
}